import { create } from "zustand";
import {
	NavigationState,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { HistoryPanelStashActions, HistoryPanelStashState } from "./types";


// Set while this store drives setToggledTab itself, so the subscriber at the bottom
// can tell its own stash/restore writes apart from panel changes made anywhere else.
let isOwnPanelWrite = false;

const withOwnPanelWrite = (write: () => void) => {
	isOwnPanelWrite = true;

	try {
		write();
	} finally {
		isOwnPanelWrite = false;
	}
};

/**
 * Owns the whole lifecycle of the panel stashed by the History detail view.
 *
 * Non-persisted by design: the stash is only meaningful while a detail view is open,
 * and that state does not survive a reload, so persisting it would let it reopen a
 * panel the user never opened this session.
 */
export const useHistoryPanelStashStore = create<
	HistoryPanelStashState & HistoryPanelStashActions
>()((set, get) => ({
	stashedTab: undefined,
	stashToggledTab: () => {
		const navigation = useNavigationStore.getState();
		const toggledTab = navigation.toggledTab;

		if (toggledTab === undefined) {
			return;
		}

		set({ stashedTab: toggledTab });
		withOwnPanelWrite(() => navigation.setToggledTab(undefined));
	},
	restoreToggledTab: () => {
		const { stashedTab } = get();

		if (stashedTab === undefined) {
			return;
		}

		set({ stashedTab: undefined });
		withOwnPanelWrite(() =>
			useNavigationStore.getState().setToggledTab(stashedTab)
		);
	},
	clearStashedTab: () =>
		set((oldValue: HistoryPanelStashState) =>
			oldValue.stashedTab === undefined ? oldValue : { stashedTab: undefined }
		),
}));

// Any panel change this store did not make invalidates the stash — whether the panel
// was closed or switched to a different one. Without the switch case a stash can
// outlive the collapse button that consumes it and later resurrect itself over the
// panel the user actually opened.
useNavigationStore.subscribe(
	(state: NavigationState, previousState: NavigationState) => {
		if (isOwnPanelWrite || state.toggledTab === previousState.toggledTab) {
			return;
		}

		useHistoryPanelStashStore.getState().clearStashedTab();
	}
);
